import { lazy, Suspense, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PhoneCall, MessageCircle } from "lucide-react";
import { Exterior } from "@/components/clinic-ui/Exterior";
import { ModeSwitcher, DepartmentDock } from "@/components/clinic-ui/ClinicNav";
import { GuideBanner, VoiceControls } from "@/components/clinic-ui/GuideHud";
import { useNarrator } from "@/components/hud/useNarrator";
import { useClinicData, useClinicGuide } from "@/components/clinic-ui/useClinic";
import { useAuth } from "@/context/AuthContext";
import { Brand, UserMenu } from "@/components/Shell";
import { Minimap } from "@/components/hud/Minimap";
import { Joystick } from "@/components/hud/Joystick";
import { WelcomeDialog } from "@/components/hud/WelcomeDialog";
import { ChatPanel } from "@/components/hud/ChatPanel";
import { DepartmentSheet } from "@/components/hud/DepartmentSheet";

const ClinicScene = lazy(() => import("@/components/clinic/ClinicScene"));

function useEnterKey(near, departments, setOpenDep) {
  useEffect(() => {
    const kd = (e) => {
      if (["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) return;
      if (e.key.toLowerCase() === "e" && near) setOpenDep(departments.find((d) => d.slug === near));
    };
    window.addEventListener("keydown", kd);
    return () => window.removeEventListener("keydown", kd);
  }, [near, departments, setOpenDep]);
}

export default function ClinicPage() {
  const { user, access, setUser } = useAuth();
  const [avatarGender, setAvatarGender] = useState(null);
  const location = useLocation();
  const pickAvatar = (g) => {
    setAvatarGender(g);
    api.patch("/auth/me/avatar", { gender: g }).then(() => setUser((u) => (u ? { ...u, avatar_gender: g } : u))).catch(() => toast.error("Impossibile salvare l'avatar"));
  };
  const [inside, setInside] = useState(() => Boolean(location.state?.enterClinic));
  const [mode, setMode] = useState("ibrida");
  const { departments, content, settings } = useClinicData();
  const [near, setNear] = useState(null);
  const [welcome, setWelcome] = useState(false);
  const [chat, setChat] = useState(false);
  const [subtitle, setSubtitle] = useState("");
  const [subsOn, setSubsOn] = useState(false);
  const isTouch = typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;

  const firstName = access?.profile?.first_name || (user && user.name?.split(" ")[0]);
  const greet = (avatarGender || user?.avatar_gender) === "donna" ? "Bentornata" : "Bentornato";
  const welcomeText = `${user ? `${greet}, ${firstName}! ` : ""}${settings.welcome_message || ""}`;

  const narrator = useNarrator(setSubtitle, false);
  const navigate = useNavigate();
  const { narrate } = narrator;
  const guide = useClinicGuide(narrator);
  const { path, guidePoints, target, tourStep, openDep, setOpenDep, guideTo, stopGuide, onArrive } = guide;

  const startTour = () => {
    if (!departments.length) return;
    setWelcome(false);
    guide.startTour(departments);
  };

  const onDoor = (dep) => {
    if (near === dep.slug) return setOpenDep(dep);
    if (mode === "libera") return toast("Avvicinati alla porta con WASD / joystick, poi premi E");
    guideTo(dep);
  };

  useEnterKey(near, departments, setOpenDep);
  const stopNarrator = narrator.stop;
  useEffect(() => {
    if (chat) stopNarrator();
  }, [chat, stopNarrator]);

  const enter = () => {
    setInside(true);
    if (settings.voice_enabled === false) return;
    if (user) narrate(`${greet}${user.name ? `, ${user.name.split(" ")[0]}` : ""}! Sono qui se hai bisogno di me.`);
    else narrate(welcomeText);
  };

  const onWelcomePick = (key) => {
    setWelcome(false);
    if (key === "esplora") return startTour();
    if (key === "chat") return setChat(true);
    const dep = departments.find((d) => d.panel_type === key);
    if (dep) guideTo(dep);
  };

  if (!inside) return <Exterior onEnter={enter} departments={departments} />;
  const nearDep = departments.find((d) => d.slug === near);

  return (
    <div className="fade-scene relative h-screen w-full overflow-hidden bg-sky-50" data-testid="clinic-interior">
      <Suspense fallback={<div className="grid h-full place-items-center text-sm text-slate-500">Caricamento clinica 3D…</div>}>
        <ClinicScene
          departments={departments} content={content} mode={mode} path={path} guidePoints={guidePoints}
          onPathStep={guide.onPathStep} onArrive={onArrive} onNear={setNear} onDoor={onDoor}
          onFloorClick={mode === "ibrida" ? guide.walkTo : undefined}
          onSelvix={() => setWelcome(true)} onSign={(slug) => (slug === "cafe" ? navigate("/cafe") : setOpenDep(departments.find((d) => d.slug === slug) || null))} onElevator={() => toast("Ascensore: i piani superiori ospiteranno i nuovi reparti configurati dall'amministratore.")}
          paused={!!openDep} avatarModelUrl={settings.avatar_model_url} targetSlug={target?.slug}
          userAvatar={user ? avatarGender || user.avatar_gender || "uomo" : null}
        />
      </Suspense>

      <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex flex-wrap items-center justify-between gap-2 p-3 sm:p-4">
        <div className="pointer-events-auto glass rounded-2xl px-3 py-2"><Brand /></div>
        <div className="pointer-events-auto order-3 w-full sm:order-none sm:w-auto"><ModeSwitcher mode={mode} setMode={(m) => { setMode(m); stopGuide(); }} /></div>
        <div className="pointer-events-auto"><UserMenu compact /></div>
      </div>

      <a href="tel:112" data-testid="emergency-112-banner" className="absolute left-1/2 top-[9.8rem] z-20 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-red-600 px-3 py-1 text-[11px] font-bold text-white shadow-lg sm:top-20">
        <PhoneCall className="h-3 w-3" /> Emergenza? Chiama il 112
      </a>

      {!welcome && <div className="absolute bottom-24 left-3 z-20 sm:bottom-6 lg:left-4"><DepartmentDock departments={departments} access={access} onPick={guideTo} target={target?.slug} /></div>}
      <div className="absolute bottom-4 right-3 z-20 hidden sm:block"><Minimap departments={departments} onPick={guideTo} targetSlug={target?.slug} /></div>

      <GuideBanner target={target} tourStep={tourStep} onStop={stopGuide} />
      <VoiceControls subsOn={subsOn} setSubsOn={setSubsOn} narrator={narrator} showTour={!tourStep} onStartTour={startTour} />

      {nearDep && !openDep && (
        <button data-testid="enter-department-prompt" onClick={() => setOpenDep(nearDep)} className="cyan-glow absolute bottom-40 left-1/2 z-20 -translate-x-1/2 rounded-full bg-sky-600 px-5 py-2.5 text-sm font-bold text-white sm:bottom-24">
          {isTouch ? "Tocca" : "Premi E"} per entrare: {nearDep.title}
        </button>
      )}

      {isTouch && mode !== "guidata" && !chat && <div className="absolute bottom-48 right-4 z-20 sm:bottom-6 sm:left-1/2 sm:right-auto sm:-translate-x-1/2"><Joystick /></div>}

      {!chat && !welcome && user && (
        <>
          <div className="glass absolute bottom-6 left-3 z-20 flex items-center gap-1 rounded-full p-1 text-[11px] font-semibold sm:bottom-20 sm:left-1/2 sm:-translate-x-1/2" data-testid="avatar-picker">
            <span className="px-2 text-slate-600">Avatar</span>
            {[["uomo", "Uomo"], ["donna", "Donna"]].map(([k, l]) => (
              <button key={k} data-testid={`avatar-pick-${k}`} onClick={() => pickAvatar(k)}
                className={`rounded-full px-3 py-1.5 transition-colors ${(avatarGender || user.avatar_gender || "uomo") === k ? "bg-emerald-600 text-white" : "text-slate-700 hover:bg-white"}`}>{l}</button>
            ))}
          </div>
        </>
      )}
      {!chat && !welcome && (
        <button data-testid="open-chat-fab" onClick={() => setChat(true)} aria-label="Parla con Doctor Selvix" title="Parla con Doctor Selvix" className="cyan-glow absolute bottom-6 right-4 z-20 grid h-14 w-14 place-items-center rounded-full bg-sky-600 text-white transition-transform hover:scale-105 sm:bottom-auto sm:right-4 sm:top-24">
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      {subsOn && subtitle && createPortal(<div data-testid="voice-subtitles" className="glass-dark pointer-events-none fixed bottom-44 left-1/2 z-[70] max-w-2xl -translate-x-1/2 rounded-xl px-4 py-2 text-center text-sm text-white sm:bottom-40">{subtitle}</div>, document.body)}

      {welcome && <WelcomeDialog message={welcomeText} onPick={onWelcomePick} onClose={() => setWelcome(false)} />}
      {chat && <ChatPanel onClose={() => setChat(false)} departments={departments} onGuide={(d) => { setChat(false); guideTo(d); }} voiceDefault={narrator.voiceOn} subs={subsOn} setSubs={setSubsOn} onSubtitle={setSubtitle} />}
      <DepartmentSheet dep={openDep} onClose={() => setOpenDep(null)} onOpenChat={() => { setOpenDep(null); setChat(true); }} />
    </div>
  );
}
